import { StepFields } from "@CommonLib/src/frontend/WizardForm/Builder/FieldsList";
import { StepsList } from "@CommonLib/src/frontend/WizardForm/Builder/StepsList";
import {
	addChoice,
	addField,
	addStep,
	deleteChoice,
	deleteField,
	deleteStep,
	editChoice,
	editField,
	editStep,
	moveChoiceDown,
	moveChoiceUp,
	moveFieldDown,
	moveFieldUp,
} from "@CommonLib/src/frontend/WizardForm/Builder/utils";
import type {
	Choice,
	Field,
	StepI,
	SubmitPayloadSchema,
	TriggerChoice,
	WizardFormConfig,
} from "@CommonLib/src/frontend/WizardForm/types";
import { WizardForm } from "@CommonLib/src/frontend/WizardForm/WizardForm";
import CloseIcon from "@mui/icons-material/Close";
import {
	AppBar,
	Button,
	Card,
	CardHeader,
	Dialog,
	Grid,
	IconButton,
	Toolbar,
	Typography,
} from "@mui/material";
import type * as React from "react";
import { useState } from "react";
import { match } from "ts-pattern";

interface WizardFormBuilderProps {
	config: WizardFormConfig;
	onConfigChange: (config: WizardFormConfig) => void;
}

const mockSendData = (_payload: SubmitPayloadSchema) => Promise.resolve();

export const WizardFormBuilder = (props: WizardFormBuilderProps) => {
	const { config, onConfigChange } = props;

	const setFormConfig = (newConfig: WizardFormConfig) => {
		onConfigChange(newConfig);
	};

	const [selectedStepId, setSelectedStepId] = useState<number | null>(null);
	const [previewOpen, setPreviewOpen] = useState(false);

	const handleListItemClick = (
		_event: React.MouseEvent<HTMLDivElement, MouseEvent>,
		index: number,
	) => {
		setSelectedStepId(index);
	};

	const onDeleteField = (stepId: number, fieldId: number) => {
		setFormConfig(deleteField(config, stepId, fieldId));
	};
	const onAddChoice = (stepId: number, fieldId: number) => {
		const newChoice: Omit<Choice, "index" | "id"> = {
			name: "New choice",
		};
		setFormConfig(addChoice(config, stepId, fieldId, newChoice));
	};
	const onDeleteChoice = (
		stepId: number,
		fieldId: number,
		choiceId: number,
	) => {
		setFormConfig(deleteChoice(config, stepId, fieldId, choiceId));
	};
	const onAddField = (stepId: number, type: Field["type"]) => {
		const choices = match(type)
			.with("select", () => [])
			.with("text", () => null)
			.with("long_text", () => null)
			.with("file", () => null)
			.exhaustive();

		const newField: Omit<Field, "index" | "id" | "identifier"> = {
			type: type,
			description: `New ${type} field description`,
			label: `New ${type} field label`,
			choices: choices,
			required: false,
		};
		setFormConfig(addField(config, stepId, newField));
	};
	const onMoveFieldUp = (stepId: number, fieldId: number) => {
		setFormConfig(moveFieldUp(config, stepId, fieldId));
	};
	const onMoveFieldDown = (stepId: number, fieldId: number) => {
		setFormConfig(moveFieldDown(config, stepId, fieldId));
	};
	const onMoveChoiceUp = (
		stepId: number,
		fieldId: number,
		choiceId: number,
	) => {
		setFormConfig(moveChoiceUp(config, stepId, fieldId, choiceId));
	};
	const onMoveChoiceDown = (
		stepId: number,
		fieldId: number,
		choiceId: number,
	) => {
		setFormConfig(moveChoiceDown(config, stepId, fieldId, choiceId));
	};
	const onAddStep = (
		parentStepId: number | null,
		triggerChoice?: TriggerChoice,
	) => {
		const newStep: Omit<StepI, "id" | "parentStepId"> = {
			name: `New Step`,
			fields: [],
			triggerChoice: triggerChoice ? triggerChoice : null,
		};
		setFormConfig(addStep(config, parentStepId, newStep));
	};
	const onDeleteStep = (stepId: number) => {
		// Important to set selectedStepId to null else the step is deleted
		// and the display of its fields on the right is broken
		setSelectedStepId(null);
		setFormConfig(deleteStep(config, stepId));
	};
	const onEditStep = (stepId: number, updates: Partial<StepI>) => {
		setFormConfig(editStep(config, stepId, updates));
	};
	const onEditField = (
		stepId: number,
		fieldId: number,
		updates: Partial<Field>,
	) => {
		setFormConfig(editField(config, stepId, fieldId, updates));
	};
	const onEditChoice = (
		stepId: number,
		fieldId: number,
		choiceId: number,
		updates: Partial<Choice>,
	) => {
		setFormConfig(editChoice(config, stepId, fieldId, choiceId, updates));
	};

	return (
		<div>
			<Grid container spacing={2}>
				{/* Left card */}
				<Grid item xs={12} md={4}>
					<Card>
						<CardHeader
							title="Form Steps"
							action={
								<Button
									variant="contained"
									color="primary"
									onClick={() => setPreviewOpen(true)}
								>
									Preview
								</Button>
							}
						/>
						<StepsList
							config={config}
							handleListItemClick={handleListItemClick}
							selectedStepId={selectedStepId}
							onAddStep={onAddStep}
							onDeleteStep={onDeleteStep}
							onEditStep={onEditStep}
						/>
					</Card>
				</Grid>

				{/* Right card */}
				<Grid item xs={12} md={8}>
					<Card>
						<CardHeader title="Step Fields" />
						<StepFields
							config={config}
							selectedStepId={selectedStepId}
							onDeleteField={onDeleteField}
							onAddChoice={onAddChoice}
							onDeleteChoice={onDeleteChoice}
							onAddField={onAddField}
							onMoveFieldUp={onMoveFieldUp}
							onMoveFieldDown={onMoveFieldDown}
							onMoveChoiceUp={onMoveChoiceUp}
							onMoveChoiceDown={onMoveChoiceDown}
							onEditField={onEditField}
							onEditChoice={onEditChoice}
						/>
					</Card>
				</Grid>
			</Grid>
			<Dialog
				fullScreen
				open={previewOpen}
				onClose={() => setPreviewOpen(false)}
			>
				<AppBar sx={{ position: "relative", bgcolor: "#111111" }}>
					<Toolbar>
						<IconButton
							edge="start"
							color="inherit"
							onClick={() => setPreviewOpen(false)}
							aria-label="close"
						>
							<CloseIcon />
						</IconButton>
						<Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
							Preview
						</Typography>
					</Toolbar>
				</AppBar>
				<div style={{ padding: "1rem" }}>
					<WizardForm
						config={config}
						previewMode={true}
						sendData={mockSendData}
					/>
				</div>
			</Dialog>
		</div>
	);
};
